// src/utils/routerLiveData.js
//
// Everything the live WAN/client control panel needs: fetching current
// WAN connections + clients, and the three actions (change priority,
// mark important, unmark important).
//
// Deliberately self-contained - unlike the old Dashboard's handleRouterClick,
// this does NOT depend on the big aggregated `data.routers` cache. Every
// function here is a plain REST call, so it works from anywhere (like the
// Site Dashboard) without needing that cache preloaded.
//
// SCOPE: Fully Managed devices only for now. WAN Managed / Data Center
// devices use a different data path in the old Dashboard (`/api/monitoring/
// interfaces` + relying on the aggregated cache for importance flags) and
// don't have a standalone "list important WAN/clients" endpoint the way FM
// devices do. Rather than guess at that path and risk showing wrong data,
// this module surfaces a clear "not supported yet" state for those device
// types. See LiveWanPanel.jsx.

import { apiFetch, apiFetchJSON } from '../api/apiClient';

const today = () => new Date().toISOString().split('T')[0];

// Cache-buster appended to every GET below. Without this, a fresh mount's
// refetch() calling the exact same URL as before could be silently served
// from the browser's HTTP cache instead of hitting the network - which
// would show the priority/importance state from BEFORE the last change,
// even though the backend's own data has already moved on. Doesn't touch
// POST actions (changeWanPriority, mark/unmarkImportantFM) - those aren't
// cached by browsers regardless.
const bust = (url) => url + (url.includes('?') ? '&' : '?') + `_=${Date.now()}`;

// ----------------------------------------------------------------------
// Fetching
// ----------------------------------------------------------------------

export async function fetchDeviceInfo(scannerId) {
  const res = await apiFetch(
    bust(`/api/monitoring/device-id?serial_number=${scannerId}`),
    { credentials: 'include' }
  );
  if (!res.ok) return null;

  const { data: deviceId } = await res.json();
  if (!deviceId) return null;

  const parts = deviceId.split(':');
  if (parts.length !== 6) return null;

  return {
    organization_id: parts[1],
    group_id: parts[3],
    device_id: parts[5],
    device_id_full: deviceId,
  };
}

/**
 * All WAN connections for a Fully Managed device, with usage merged in
 * and an `_is_important` flag on each - exactly what WanTable expects.
 */
export async function fetchAllWanFM(scannerId, routerName, scope = 'admin') {
  const [wanConnections, deviceInfo] = await Promise.all([
    apiFetchJSON(bust(scope === 'client' ? `/api/v2/client/fm-wan/${scannerId}/all` : `/api/router/${scannerId}/wan/all`)),
    fetchDeviceInfo(scannerId),
  ]);

  const allWan = wanConnections.wan_connections || [];

  // Usage + importance, best-effort - a failure here shouldn't block
  // showing the WAN list itself.
  let usageByWanName = {};
  let allUsageData = [];
  let importantWanNames = new Set();

  try {
    const [usageJson, importantJson] = await Promise.all([
      // BUGFIX: was passing the raw serial (scannerId) here, which
      // /api/monitoring/data-usage doesn't accept - it needs the resolved
      // internal device id (device_id_full), same as every other usage
      // call in this file (fetchAllClientsFM, fetchAllWanManaged,
      // fetchAllClientsManaged). The mismatch made this call silently
      // fail (swallowed by the catch below), so FM WAN usage always
      // showed 0.00 GB regardless of actual usage.
      apiFetchJSON(bust(`/api/monitoring/data-usage?device=${deviceInfo?.device_id_full}&date=${today()}`)),
      apiFetchJSON(bust(scope === 'client' ? `/api/v2/client/fm-wan/${scannerId}/important` : `/api/router/${scannerId}/wan/important`)),
    ]);

    allUsageData = usageJson.data || [];
    usageByWanName = Object.fromEntries(
      allUsageData.map((u) => [(u.wan_name || '').toLowerCase(), u])
    );

    importantWanNames = new Set(
      (importantJson.wan_connections || []).map((w) => w.item_key || w.item_name)
    );
  } catch {
    // best-effort only, continue without usage/importance
  }

  // wanDetail - same live-derivation approach already proven for WM/DC,
  // applied here too. get_wan_interface_detail() (the old path, still
  // used by fetchWanInterfaceDetail below) reads a collector-archived
  // file that may not exist - this doesn't depend on that at all.
  let wanDetail = { starlink_by_interface: {}, cellular_by_interface: {} };
  try {
    const allData = await apiFetchJSON(bust(`/api/monitoring/all-data?device=${deviceInfo.device_id_full}&date=${today()}`));
    const starlinkStatus = allData?.starlinkData?.starlink_status || [];
    for (const s of starlinkStatus) {
      if (!s.wan_interface_name) continue;
      wanDetail.starlink_by_interface[s.wan_interface_name] = {
        ut_id: s.ut_id,
        ethSpeedMbps: s.ethSpeedMbps,
        popPingLatencyMs: s.popPingLatencyMs,
        boresightElevationDeg: s.boresightElevationDeg,
        boresightAzimuthDeg: s.boresightAzimuthDeg,
        obstruction_stats: s.obstruction_stats,
        isSnrAboveNoiseFloor: s.isSnrAboveNoiseFloor,
      };
    }
    const cellularInterfaces = allData?.interfaces?.cellular_interfaces || [];
    for (const c of cellularInterfaces) {
      if (!c.name) continue;
      wanDetail.cellular_by_interface[c.name] = {
        imei: c.imei,
        carrier_name: c.carrier_name,
        network: c.network,
        sims: c.sims || [],
      };
    }
  } catch {
    // best-effort - card/table still renders without type-specific detail
  }

  const mergedWanConnections = allWan.map((wan) => {
    const wanName = (wan.name || wan.interface_name || '').toLowerCase();
    const usage = usageByWanName[wanName];

    return {
      ...wan,
      download: usage?.download || 0,
      upload: usage?.upload || 0,
      rx: usage?.rx || 0,
      tx: usage?.tx || 0,
      _usage: usage,
      _is_important: importantWanNames.has(wan.name || wan.interface_name),
    };
  });

  const importantItems = [...importantWanNames].map((name) => ({
    item_type: 'wan_interface',
    item_key: name,
    name,
    interface_name: name,
    is_important: true,
  }));

  return {
    wanConnections: mergedWanConnections,
    importantItems,
    deviceInfo,
    dataUsage: allUsageData,
    wanDetail,
  };
}

/**
 * All clients for a Fully Managed device, with usage merged in and an
 * `_is_important` flag on each - exactly what ClientTable expects.
 */
export async function fetchAllClientsFM(scannerId, scope = 'admin') {
  const devicesJson = await apiFetchJSON(bust(scope === 'client' ? `/api/v2/client/fm-devices/${scannerId}/all` : `/api/router/${scannerId}/devices/all`));
  const fmClients = devicesJson.devices || [];

  let usageByMac = {};
  let importantMacs = new Set();

  try {
    const deviceInfo = await fetchDeviceInfo(scannerId);
    const [usageJson, importantJson] = await Promise.all([
      deviceInfo
        ? apiFetchJSON(bust(`/api/monitoring/client-usage?device=${deviceInfo.device_id_full}&date=${today()}`))
        : Promise.resolve({ data: [] }),
      apiFetchJSON(bust(scope === 'client' ? `/api/v2/client/fm-devices/${scannerId}/important` : `/api/router/${scannerId}/devices/important`)),
    ]);

    usageByMac = Object.fromEntries(
      (usageJson.data || [])
        .filter((c) => c.mac)
        .map((c) => [c.mac.toLowerCase(), c])
    );

    importantMacs = new Set(
      (importantJson.devices || []).map((d) => (d.mac || '').toLowerCase())
    );
  } catch {
    // best-effort only, continue without usage/importance
  }

  // NOTE ON DUPLICATE MACS: a physical client bridged onto more than one
  // VLAN/subnet appears as more than one row in `fmClients` (confirmed on
  // real fleet data - e.g. one device appears 5x across different IPs).
  // Every one of those rows genuinely is that important client, so it's
  // correct for each to get `_is_important: true` here - this function
  // intentionally does NOT dedupe by MAC. Counting/displaying "important
  // clients" as one entry per physical device (matching the device-level
  // count /devices/important itself returns) is a presentation concern,
  // handled by dedupeByMac() in LiveWanPanel.jsx, not here - keep it that
  // way so this function's output stays a faithful 1:1 map of
  // `/devices/all`.
  const clients = fmClients.map((client) => {
    const mac = (client.mac || '').toLowerCase();
    const usage = usageByMac[mac];

    return {
      ...client,
      manufacturer: usage?.manufacturer || client.vendor || 'Unknown',
      client_name: usage?.client_name || client.hostname || 'Unknown',
      rx: usage?.rx || client.rx || 0,
      tx: usage?.tx || client.tx || 0,
      download: usage?.download || client.download || 0,
      upload: usage?.upload || client.upload || 0,
      _usage: usage,
      _is_important: importantMacs.has(mac),
    };
  });

  const importantItems = [...importantMacs].map((mac) => ({
    item_type: 'client',
    item_key: mac,
    mac,
    is_important: true,
  }));

  return { clients, importantItems };
}

/**
 * All WAN + cellular interfaces for a WAN Managed / Data Center device.
 *
 * Uses /api/monitoring/interfaces - the same endpoint the historical
 * monitoring page uses - which returns { data: { wan_interfaces: [...],
 * cellular_interfaces: [...] } }. Confirmed by reading get_interfaces()
 * directly: priority is already normalized to the same 0-3/'Disable'
 * format WanTable expects, so no conversion needed here.
 *
 * Importance comes from the new device-agnostic endpoint, NOT
 * /api/router/.../wan/important - that one 404s for every WM/DC device
 * because it depends on load_all_reports(), which only contains devices
 * with a comprehensive_report.json (FM only, by construction).
 */
export async function fetchAllWanManaged(scannerId, scope = 'admin') {
  const deviceInfo = await fetchDeviceInfo(scannerId);
  if (!deviceInfo) throw new Error('Could not resolve device info for this router');

  const [allData, importance] = await Promise.all([
    apiFetchJSON(bust(`/api/monitoring/all-data?device=${deviceInfo.device_id_full}&date=${today()}`)),
    apiFetchJSON(bust(`/api/v2/${scope}/wan-importance/${scannerId}`)),
  ]);

  // BUGFIX: previously called /api/monitoring/interfaces separately and
  // read usage from allData.usage_data.wan_usage - neither is correct.
  // Confirmed against a real response: /api/monitoring/all-data already
  // includes interfaces.wan_interfaces/cellular_interfaces (same fields
  // /api/monitoring/interfaces returns, so that call was redundant), and
  // per-WAN usage lives at the TOP-LEVEL key `dataUsage`, not nested
  // under `usage_data` (which doesn't exist in the live response at all -
  // that was the actual reason usage never showed).
  const wanInterfaces = allData?.interfaces?.wan_interfaces || [];
  const cellularInterfaces = allData?.interfaces?.cellular_interfaces || [];
  const importantNames = new Set(importance.wan || []);

  // dataUsage values are already in the same unit the FM usage endpoint
  // uses (confirmed by comparing against hourlyWanUsagesLogs for the same
  // WAN - same order of magnitude, not raw bytes) - no conversion needed,
  // unlike client usage below.
  const usageByName = {};
  for (const u of (allData?.dataUsage || [])) {
    usageByName[u.wan_name] = { download: u.download || 0, upload: u.upload || 0 };
  }

  const tag = (list, kind) =>
    list.map((iface) => {
      const usage = usageByName[iface.name];
      return {
        ...iface,
        _interface_kind: kind, // 'wan' | 'cellular' - used for type-specific detail
        _is_important: importantNames.has(iface.name),
        download: usage?.download || 0,
        upload: usage?.upload || 0,
      };
    });

  const wanConnections = [...tag(wanInterfaces, 'wan'), ...tag(cellularInterfaces, 'cellular')];

  const importantItems = [...importantNames].map((name) => ({
    item_type: 'wan_interface',
    item_key: name,
    name,
    interface_name: name,
    is_important: true,
  }));

  // wanDetail - built directly from this SAME live response instead of a
  // separate call to a backend endpoint that reads a collector-archived
  // file from disk. That file only exists if the collector has run and
  // archived at least once - unreliable on staging (deliberately not
  // running the collector there). starlinkData.starlink_status carries
  // exactly the fields the detail modal needs (confirmed against a real
  // response: ut_id, wan_interface_name, boresightElevationDeg/AzimuthDeg,
  // ethSpeedMbps, popPingLatencyMs, obstruction_stats).
  const wanDetail = { starlink_by_interface: {}, cellular_by_interface: {} };
  const starlinkStatus = allData?.starlinkData?.starlink_status || [];
  for (const s of starlinkStatus) {
    if (!s.wan_interface_name) continue;
    wanDetail.starlink_by_interface[s.wan_interface_name] = {
      ut_id: s.ut_id,
      ethSpeedMbps: s.ethSpeedMbps,
      popPingLatencyMs: s.popPingLatencyMs,
      boresightElevationDeg: s.boresightElevationDeg,
      boresightAzimuthDeg: s.boresightAzimuthDeg,
      obstruction_stats: s.obstruction_stats,
      isSnrAboveNoiseFloor: s.isSnrAboveNoiseFloor,
    };
  }
  for (const c of cellularInterfaces) {
    if (!c.name) continue;
    wanDetail.cellular_by_interface[c.name] = {
      imei: c.imei,
      carrier_name: c.carrier_name,
      network: c.network,
      sims: c.sims || [],
    };
  }

  return { wanConnections, importantItems, deviceInfo, wanDetail };
}

/**
 * Clients for a WAN Managed / Data Center device. Unlike FM, there's no
 * standalone "all clients" REST endpoint for these device types - the
 * live client list lives in the same daily device_data.json the
 * historical monitoring page reads, under client_data.top_clients.
 */
export async function fetchAllClientsManaged(scannerId, scope = 'admin') {
  const deviceInfo = await fetchDeviceInfo(scannerId);
  if (!deviceInfo) throw new Error('Could not resolve device info for this router');

  const [reportJson, importance] = await Promise.all([
    apiFetchJSON(bust(`/api/monitoring/all-data?device=${deviceInfo.device_id_full}&date=${today()}`)),
    apiFetchJSON(bust(`/api/v2/${scope}/wan-importance/${scannerId}`)),
  ]);

  // BUGFIX: confirmed against a real response - the live endpoint returns
  // clients under the TOP-LEVEL key `clientUsage`, not the nested
  // `client_data.top_clients` path (that's the archived-file format, a
  // different shape than what this API actually returns). rx/tx here are
  // confirmed real bytes (~16GB range for a real client), so the MB
  // conversion below was already correct - it just never ran because the
  // path above it resolved to nothing.
  const rawClients = reportJson?.clientUsage || [];
  const importantMacs = new Set((importance.clients || []).map((m) => (m || '').toLowerCase()));

  // Same intentional non-dedupe as fetchAllClientsFM above - a multi-VLAN
  // client can produce more than one row here, each correctly flagged.
  // Collapsing duplicates for display/counting is LiveWanPanel.jsx's job
  // (dedupeByMac), so this consumer gets the same behavior FM does.
  const clients = rawClients.map((client) => ({
    ...client,
    download: (client.rx || 0) / 1e6,
    upload: (client.tx || 0) / 1e6,
    _is_important: importantMacs.has((client.mac || '').toLowerCase()),
  }));

  const importantItems = [...importantMacs].map((mac) => ({
    item_type: 'client',
    item_key: mac,
    mac,
    is_important: true,
  }));

  return { clients, importantItems };
}

/**
 * Type-specific detail per WAN interface name: Starlink boresight/
 * obstruction/kit info, cellular IMEI/ICCID/IMSI. Works for any device
 * type - reads the daily archive directly rather than depending on
 * comprehensive_report.json.
 */
export function fetchWanInterfaceDetail(scannerId, scope = 'admin') {
  return apiFetchJSON(bust(`/api/v2/${scope}/wan-detail/${scannerId}`));
}

// ----------------------------------------------------------------------
// Actions
// ----------------------------------------------------------------------

/**
 * Same /api/wan/priority endpoint the old Dashboard uses. This is
 * device-type-agnostic on the backend, so it's reused as-is.
 */
export async function changeWanPriority(serialNumber, priorityPayload) {
  const deviceInfo = await fetchDeviceInfo(serialNumber);
  if (!deviceInfo) throw new Error('Could not resolve device info for this router');

  const response = await apiFetch('/api/wan/priority', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      organization_id: deviceInfo.organization_id,
      group_id: deviceInfo.group_id,
      device_id: deviceInfo.device_id,
      priorities: priorityPayload,
    }),
  });

  const result = await response.json();

  // BUGFIX: the backend's own `success` flag only reflects whether ITS
  // request succeeded, not whether the downstream Peplink/IC2 call did -
  // it has been observed returning `success: true` at the top level while
  // `data.data` nested underneath contains `{ stat: 'fail', code: 400,
  // message: 'Invalid priority' }`. Don't trust the outer flag alone;
  // that nested envelope is where the real device-side result lives.
  const nested = result?.data?.data;
  if (nested && (nested.stat === 'fail' || (nested.code && nested.code !== 200))) {
    throw new Error(nested.message || 'Failed to update priorities');
  }
  if (!result.success) throw new Error(result.error || 'Failed to update priorities');
  return true;
}

export async function markImportantFM(serialNumber, itemType, item) {
  const endpoint =
    itemType === 'client'
      ? '/api/importance/fully-managed/mark-client'
      : '/api/importance/fully-managed/mark-wan';

  const body =
    itemType === 'client'
      ? {
          serial_number: serialNumber,
          mac_address: item.mac || item.mac_address,
          client_name: item.hostname || item.client_name,
          ip_address: item.ip || item.ip_address,
        }
      : {
          serial_number: serialNumber,
          connection_id: item.name || item.interface_name,
          connection_name: item.connection_name || item.name,
          router_ip: item.ip || item.ip_address,
        };

  const response = await apiFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Failed to mark item as important');
  return true;
}

export async function unmarkImportantFM(serialNumber, itemType, item) {
  const endpoint =
    itemType === 'client'
      ? '/api/importance/fully-managed/unmark-client'
      : '/api/importance/fully-managed/unmark-wan';

  const body =
    itemType === 'client'
      ? { serial_number: serialNumber, mac_address: item.mac || item.mac_address }
      : { serial_number: serialNumber, interface_name: item.name || item.interface_name };

  const response = await apiFetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Failed to unmark item as important');
  return true;
}
